import {
  DataProvider,
  GetListParams,
  GetOneParams,
  GetManyParams,
  GetManyReferenceParams,
  CreateParams,
  UpdateParams,
  UpdateManyParams,
  DeleteParams,
  DeleteManyParams,
} from 'react-admin';
import { getSupabaseAuth } from '../lib/supabase';

// Supabase Auth 클라이언트 (Anon Key)
// 브라우저에서는 Anon Key를 사용하고 RLS로 권한을 제어해야 합니다
const supabase = getSupabaseAuth();

// 리소스명을 Supabase 테이블명으로 매핑
const resourceToTable: Record<string, string> = {
  products: 'Product',
  categories: 'Category',
  orders: 'orders', // 소문자 테이블 사용 (실제 데이터가 있는 테이블)
  users: 'User',
  addresses: 'Address',
  banners: 'Banner',
  reviews: 'Review',
  coupons: 'Coupon',
  notices: 'Notice',
  inquiries: 'Inquiry',
  brands: 'Brand',
  'deal-featured': 'deal_featured',
  'user-coupons': 'UserCoupon',
  'point-history': 'PointHistory',
  faqs: 'FAQ',
  'partnership-inquiries': 'PartnershipInquiry',
  'membership-configs': 'MembershipConfig',
};

const getTableName = (resource: string): string => {
  return resourceToTable[resource] || resource;
};

export const dataProvider: DataProvider = {
  // 목록 조회
  getList: async (resource: string, params: GetListParams) => {
    const tableName = getTableName(resource);
    const { page = 1, perPage = 10 } = params.pagination || {};
    const { field = 'id', order = 'DESC' } = params.sort || {};
    const { filter = {} } = params;

    try {
      let query = supabase
        .from(tableName)
        .select('*', { count: 'exact' });

      // reviews 상품명/작성자명 검색을 위한 사전 처리
      let reviewProductIds: string[] | null = null;
      let reviewUserIds: string[] | null = null;
      if (resource === 'reviews' && (filter.q || filter.search)) {
        const searchValue = filter.q || filter.search;

        // 상품명으로 검색
        const productSearch = await supabase
          .from('Product')
          .select('id')
          .ilike('name', `%${searchValue}%`);

        if (productSearch.data && productSearch.data.length > 0) {
          reviewProductIds = productSearch.data.map((p: any) => p.id);
        }

        // 작성자명으로 검색
        const userSearch = await supabase
          .from('User')
          .select('id')
          .ilike('name', `%${searchValue}%`);

        if (userSearch.data && userSearch.data.length > 0) {
          reviewUserIds = userSearch.data.map((u: any) => u.id);
        }
      }

      // 필터 적용
      Object.keys(filter).forEach((key) => {
        const value = filter[key];
        if (value !== undefined && value !== null && value !== '') {
          // 검색 쿼리 처리 (q 또는 search)
          if (key === 'q' || key === 'search') {
            // products 테이블은 name, brand, id, description 통합 검색
            if (resource === 'products') {
              query = query.or(`name.ilike.%${value}%,brand.ilike.%${value}%,id.ilike.%${value}%,description.ilike.%${value}%`);
            } else if (resource === 'reviews') {
              // 상품명 또는 작성자명으로 찾은 경우
              const hasProductMatch = reviewProductIds && reviewProductIds.length > 0;
              const hasUserMatch = reviewUserIds && reviewUserIds.length > 0;

              if (hasProductMatch && hasUserMatch) {
                // 둘 다 있으면 OR 조건
                const allIds = [...(reviewProductIds || [])];
                const userIdCondition = reviewUserIds!.map(id => `userId.eq.${id}`).join(',');
                const productIdCondition = reviewProductIds!.map(id => `productId.eq.${id}`).join(',');
                query = query.or(`${productIdCondition},${userIdCondition}`);
              } else if (hasProductMatch) {
                query = query.in('productId', reviewProductIds!);
              } else if (hasUserMatch) {
                query = query.in('userId', reviewUserIds!);
              } else {
                // 둘 다 못 찾으면 title, content로 검색
                query = query.or(`title.ilike.%${value}%,content.ilike.%${value}%`);
              }
            } else if (resource === 'inquiries') {
              // inquiries 테이블은 title과 content 검색
              query = query.or(`title.ilike.%${value}%,content.ilike.%${value}%`);
            } else if (resource === 'partnership-inquiries') {
              // partnership-inquiries는 name, email, companyName으로 검색
              query = query.or(`name.ilike.%${value}%,email.ilike.%${value}%,companyName.ilike.%${value}%`);
            } else {
              // 기타 테이블은 name과 description 검색
              query = query.or(`name.ilike.%${value}%,description.ilike.%${value}%`);
            }
          } else if (typeof value === 'boolean') {
            query = query.eq(key, value);
          } else {
            query = query.eq(key, value);
          }
        }
      });

      // 정렬 적용
      query = query.order(field, { ascending: order === 'ASC' });

      // 페이지네이션 적용
      const from = (page - 1) * perPage;
      const to = page * perPage - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        total: count || 0,
      };
    } catch (error) {
      console.error(`Error fetching ${resource} list:`, error);
      throw error;
    }
  },

  // 단일 항목 조회
  getOne: async (resource: string, params: GetOneParams) => {
    // customHandlers에 getOne이 있으면 그것 사용
    if (customHandlers[resource]?.getOne) {
      return customHandlers[resource].getOne(resource, params);
    }

    const tableName = getTableName(resource);

    try {
      let query = supabase.from(tableName).select('*').eq('id', params.id);

      // Product의 경우 이미지 관계 포함
      if (resource === 'products') {
        query = supabase
          .from(tableName)
          .select('*, ProductImage(*)')
          .eq('id', params.id);
      }

      // deal-featured는 relation 때문에 명시적으로 필드 지정
      if (resource === 'deal-featured') {
        query = supabase
          .from(tableName)
          .select('id,product_id,display_order,tag,deal_end_time,is_active,created_at,updated_at')
          .eq('id', params.id);
      }

      const { data, error } = await query.single();

      if (error) throw error;

      // ProductImage를 images 필드로 매핑
      if (resource === 'products' && data) {
        data.images = data.ProductImage || [];
        delete data.ProductImage;
      }

      return { data };
    } catch (error) {
      console.error(`Error fetching ${resource} ${params.id}:`, error);
      throw error;
    }
  },

  // 여러 항목 조회
  getMany: async (resource: string, params: GetManyParams) => {
    const tableName = getTableName(resource);

    try {
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .in('id', params.ids);

      if (error) throw error;

      return { data: data || [] };
    } catch (error) {
      console.error(`Error fetching multiple ${resource}:`, error);
      throw error;
    }
  },

  // 참조 목록 조회
  getManyReference: async (resource: string, params: GetManyReferenceParams) => {
    const tableName = getTableName(resource);
    const { page = 1, perPage = 10 } = params.pagination || {};
    const { field = 'id', order = 'DESC' } = params.sort || {};
    const { filter = {} } = params;

    try {
      let query = supabase
        .from(tableName)
        .select('*', { count: 'exact' })
        .eq(params.target, params.id);

      // 필터 적용
      Object.keys(filter).forEach((key) => {
        const value = filter[key];
        if (value !== undefined && value !== null) {
          query = query.eq(key, value);
        }
      });

      // 정렬 적용
      query = query.order(field, { ascending: order === 'ASC' });

      // 페이지네이션 적용
      const from = (page - 1) * perPage;
      const to = page * perPage - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        total: count || 0,
      };
    } catch (error) {
      console.error(`Error fetching ${resource} references:`, error);
      throw error;
    }
  },

  // 생성
  create: async (resource: string, params: CreateParams) => {
    const tableName = getTableName(resource);

    try {
      // coupons, notices, banners는 id와 updatedAt 자동 생성
      let createData = params.data;
      if (resource === 'coupons' || resource === 'notices' || resource === 'banners') {
        const now = new Date().toISOString();
        createData = {
          id: crypto.randomUUID(),
          ...params.data,
          updatedAt: now,
        };
      }

      const { data, error } = await supabase
        .from(tableName)
        .insert([createData])
        .select('*')
        .single();

      if (error) throw error;

      return { data };
    } catch (error) {
      console.error(`Error creating ${resource}:`, error);
      throw error;
    }
  },

  // 수정
  update: async (resource: string, params: UpdateParams) => {
    const tableName = getTableName(resource);

    try {
      // coupons와 notices는 updatedAt 자동 업데이트
      let updateData = params.data;
      if (resource === 'coupons' || resource === 'notices') {
        updateData = {
          ...params.data,
          updatedAt: new Date().toISOString(),
        };
      }

      // products 업데이트 시 관계 필드 및 폼 전용 필드 제거
      if (resource === 'products') {
        const {
          images,
          ProductImage,
          Category,
          _parentCategory,
          shippingType,
          shippingFee,
          ...rest
        } = params.data;
        updateData = {
          ...rest,
          updatedAt: new Date().toISOString(),
        };
      }

      console.log(`[DataProvider] Updating ${resource} ${params.id}:`, updateData);

      const { data, error } = await supabase
        .from(tableName)
        .update(updateData)
        .eq('id', params.id)
        .select('*')
        .single();

      if (error) {
        console.error(`[DataProvider] Supabase error:`, error);
        throw error;
      }

      console.log(`[DataProvider] Update successful:`, data);
      return { data };
    } catch (error) {
      console.error(`Error updating ${resource} ${params.id}:`, error);
      throw error;
    }
  },

  // 여러 항목 수정
  updateMany: async (resource: string, params: UpdateManyParams) => {
    const tableName = getTableName(resource);

    console.log(`[DataProvider] updateMany ${resource}:`, {
      ids: params.ids,
      data: params.data,
      tableName,
      timestamp: new Date().toISOString(),
    });

    try {
      // 업데이트할 데이터 준비
      let updateData = params.data;
      
      // 타임스탬프 추가 (일부 테이블용)
      if (resource === 'products') {
        // images, ProductImage는 별도 테이블과의 관계이므로 제외
        const { images, ProductImage, ...rest } = params.data;
        updateData = {
          ...rest,
          updatedAt: new Date().toISOString(),
        };
      }

      console.log(`[DataProvider] Sending update request:`, {
        table: tableName,
        ids: params.ids,
        updateData,
      });

      // 업데이트 실행 및 결과 조회
      const { data: updatedData, error } = await supabase
        .from(tableName)
        .update(updateData)
        .in('id', params.ids)
        .select();

      if (error) {
        console.error(`[DataProvider] updateMany Supabase error:`, {
          error,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
        throw error;
      }

      console.log(`[DataProvider] updateMany success:`, {
        updatedCount: updatedData?.length || 0,
        requestedCount: params.ids.length,
        ids: params.ids,
        updatedData: updatedData,
      });

      // 업데이트된 행 수가 요청한 ID 수와 다르면 경고
      if (updatedData && updatedData.length !== params.ids.length) {
        console.warn(`[DataProvider] Warning: Updated ${updatedData.length} rows but requested ${params.ids.length}`);
      }

      return { data: params.ids };
    } catch (error: any) {
      console.error(`[DataProvider] Error updating multiple ${resource}:`, {
        error,
        message: error?.message,
        stack: error?.stack,
      });
      throw error;
    }
  },

  // 삭제
  delete: async (resource: string, params: DeleteParams) => {
    const tableName = getTableName(resource);

    try {
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', params.id);

      if (error) throw error;

      return { data: params.previousData as any };
    } catch (error) {
      console.error(`Error deleting ${resource} ${params.id}:`, error);
      throw error;
    }
  },

  // 여러 항목 삭제
  deleteMany: async (resource: string, params: DeleteManyParams) => {
    const tableName = getTableName(resource);

    try {
      const { error } = await supabase
        .from(tableName)
        .delete()
        .in('id', params.ids);

      if (error) throw error;

      return { data: params.ids };
    } catch (error) {
      console.error(`Error deleting multiple ${resource}:`, error);
      throw error;
    }
  },
};

// 특정 리소스에 대한 커스텀 처리
const customHandlers: Record<string, Partial<DataProvider>> = {
  // 주문 관리 - order_items, Product, User 관계 포함
  'orders': {
    getList: async (_resource: string, params: GetListParams) => {
      const { page = 1, perPage = 10 } = params.pagination || {};
      const { field = 'created_at', order = 'DESC' } = params.sort || {};
      const { filter = {} } = params;

      try {
        // 상품명 검색이 있는 경우 먼저 order_items에서 order_id 목록을 가져옴
        let productNameOrderIds: string[] | null = null;
        if (filter.productName) {
          console.log('[DataProvider] 상품명 검색 - order_items 테이블에서 검색:', filter.productName);
          const { data: orderItems, error: itemsError } = await supabase
            .from('order_items')
            .select('order_id')
            .ilike('product_name', `%${filter.productName}%`);

          if (itemsError) {
            console.error('[DataProvider] order_items 검색 오류:', itemsError);
          } else if (orderItems && orderItems.length > 0) {
            // 중복 제거
            productNameOrderIds = [...new Set(orderItems.map(item => item.order_id))];
            console.log('[DataProvider] 상품명 검색 결과 order_id 목록:', productNameOrderIds.length, '건');
          } else {
            // 검색 결과가 없으면 빈 배열 반환
            console.log('[DataProvider] 상품명 검색 결과 없음');
            return { data: [], total: 0 };
          }
        }

        let query = supabase
          .from('orders')
          .select(`
            *,
            order_items(
              id,
              product_name,
              product_price,
              discount_price,
              quantity,
              subtotal
            )
          `, { count: 'exact' });

        // 상품명 검색 결과가 있으면 order_id로 필터링
        if (productNameOrderIds) {
          query = query.in('id', productNameOrderIds);
        }

        // 필터 적용
        Object.keys(filter).forEach((key) => {
          const value = filter[key];
          if (value !== undefined && value !== null && value !== '') {
            // 주문번호 검색
            if (key === 'orderNumber') {
              query = query.ilike('order_number', `%${value}%`);
            }
            // 주문자명 검색
            else if (key === 'ordererName' || key === 'orderer_name') {
              query = query.ilike('orderer_name', `%${value}%`);
            }
            // 수취인명 검색
            else if (key === 'receiverName' || key === 'recipient_name') {
              query = query.ilike('recipient_name', `%${value}%`);
            }
            // 수취인 연락처 검색
            else if (key === 'receiverPhone' || key === 'recipient_phone') {
              query = query.ilike('recipient_phone', `%${value}%`);
            }
            // 연락처 검색 (주문자 또는 수취인)
            else if (key === 'phone') {
              query = query.or(`orderer_phone.ilike.%${value}%,recipient_phone.ilike.%${value}%`);
            }
            // 주소 검색
            else if (key === 'address') {
              query = query.or(`address.ilike.%${value}%,address_detail.ilike.%${value}%`);
            }
            // 회원명 검색 (orderer_name으로 대체)
            else if (key === 'userName') {
              query = query.ilike('orderer_name', `%${value}%`);
            }
            // 회원 이메일 검색
            else if (key === 'userEmail') {
              query = query.ilike('orderer_email', `%${value}%`);
            }
            // 상품명 검색 - order_items 테이블에서 검색 후 order_id 목록으로 필터링
            else if (key === 'productName') {
              // productName 검색은 별도 처리 필요 - 아래에서 처리
              console.log('[DataProvider] productName 검색:', value);
            }
            // 주문상태
            else if (key === 'status') {
              query = query.eq('status', value);
            }
            // 결제수단
            else if (key === 'paymentMethod' || key === 'payment_method') {
              query = query.eq('payment_method', value);
            }
            // 배송상태
            else if (key === 'shippingStatus' || key === 'shipping_status') {
              query = query.eq('shipping_status', value);
            }
            // 금액 범위
            else if (key === 'minAmount') {
              query = query.gte('total_amount', value);
            }
            else if (key === 'maxAmount') {
              query = query.lte('total_amount', value);
            }
            // 날짜 범위 (ISO 문자열 또는 Date 객체 지원)
            else if (key === 'startDate') {
              const startDate = typeof value === 'string' ? value : new Date(value).toISOString();
              console.log('[DataProvider] startDate filter:', startDate);
              query = query.gte('created_at', startDate);
            }
            else if (key === 'endDate') {
              // endDate는 해당 날짜의 끝(23:59:59.999)까지 포함해야 함
              let endDateStr: string;
              if (typeof value === 'string') {
                // ISO 문자열인 경우 그대로 사용 (이미 endDate 설정 시 23:59:59.999로 설정됨)
                endDateStr = value;
              } else {
                const endDate = new Date(value);
                endDate.setHours(23, 59, 59, 999);
                endDateStr = endDate.toISOString();
              }
              console.log('[DataProvider] endDate filter:', endDateStr);
              query = query.lte('created_at', endDateStr);
            }
            // 기타 필드는 snake_case로 직접 매칭
            else {
              query = query.eq(key, value);
            }
          }
        });

        // 정렬 적용 (camelCase → snake_case 변환)
        let sortField = field;
        if (field === 'orderNumber') sortField = 'order_number';
        else if (field === 'totalAmount') sortField = 'total_amount';
        else if (field === 'paymentMethod') sortField = 'payment_method';
        else if (field === 'createdAt') sortField = 'created_at';

        query = query.order(sortField, { ascending: order === 'ASC' });

        // 페이지네이션 적용
        const from = (page - 1) * perPage;
        const to = page * perPage - 1;
        query = query.range(from, to);

        const { data, error, count } = await query;

        if (error) throw error;

        // snake_case → camelCase 변환
        const transformedData = (data || []).map((order: any) => ({
          id: order.id,
          orderNumber: order.order_number,
          userId: order.user_id,
          userName: order.orderer_name, // 주문자명을 userName으로 사용
          userEmail: order.orderer_email,
          ordererName: order.orderer_name,
          ordererPhone: order.orderer_phone,
          ordererEmail: order.orderer_email,
          recipientName: order.recipient_name,
          recipientPhone: order.recipient_phone,
          postcode: order.postcode,
          address: order.address,
          addressDetail: order.address_detail,
          status: order.status,
          paymentStatus: order.payment_status,
          paymentMethod: order.payment_method,
          paymentKey: order.payment_key,
          totalAmount: order.total_amount,
          productAmount: order.product_amount,
          shippingFee: order.shipping_fee,
          discountAmount: order.discount_amount,
          couponDiscount: order.coupon_discount,
          pointsUsed: order.points_used,
          finalAmount: order.final_amount,
          shippingStatus: order.shipping_status,
          shippingMemo: order.shipping_memo,
          trackingNumber: order.tracking_number,
          shippingCompany: order.shipping_company,
          shippedAt: order.shipped_at,
          deliveredAt: order.delivered_at,
          paidAt: order.paid_at,
          cancelledAt: order.cancelled_at,
          refundedAt: order.refunded_at,
          created_at: order.created_at,
          updated_at: order.updated_at,
          items: order.order_items || [],
        }));

        return {
          data: transformedData,
          total: count || 0,
        };
      } catch (error) {
        console.error('Error fetching orders list:', error);
        throw error;
      }
    },
    getOne: async (_resource: string, params: GetOneParams) => {
      try {
        const { data, error } = await supabase
          .from('orders')
          .select(`
            *,
            order_items(
              id,
              product_name,
              product_price,
              discount_price,
              quantity,
              subtotal
            )
          `)
          .eq('id', params.id)
          .single();

        if (error) throw error;
        if (!data) throw new Error('Order not found');

        // snake_case → camelCase 변환 (getList와 동일한 로직)
        const transformedData = {
          id: data.id,
          orderNumber: data.order_number,
          userId: data.user_id,
          userName: data.orderer_name,
          userEmail: data.orderer_email,
          userPhone: data.orderer_phone, // OrderShow에서 사용
          ordererName: data.orderer_name,
          ordererPhone: data.orderer_phone,
          ordererEmail: data.orderer_email,
          recipientName: data.recipient_name,
          recipientPhone: data.recipient_phone,
          postcode: data.postcode,
          address: data.address,
          addressDetail: data.address_detail,
          status: data.status,
          paymentStatus: data.payment_status,
          paymentMethod: data.payment_method,
          paymentKey: data.payment_key,
          isPaid: data.payment_status === 'PAID', // 결제완료 여부
          totalAmount: data.total_amount,
          subtotal: data.product_amount, // OrderShow에서 상품금액으로 사용
          productAmount: data.product_amount,
          shippingFee: data.shipping_fee,
          discountAmount: data.discount_amount,
          couponDiscount: data.coupon_discount,
          pointsUsed: data.points_used,
          pointUsed: data.points_used, // OrderShow에서 사용
          finalAmount: data.final_amount,
          shippingStatus: data.shipping_status,
          shippingMemo: data.shipping_memo,
          deliveryRequest: data.shipping_memo, // OrderShow에서 사용
          trackingNumber: data.tracking_number,
          shippingCompany: data.shipping_company,
          adminMemo: data.admin_memo,
          shippedAt: data.shipped_at,
          deliveredAt: data.delivered_at,
          paidAt: data.paid_at,
          cancelledAt: data.cancelled_at,
          refundedAt: data.refunded_at,
          created_at: data.created_at,
          updated_at: data.updated_at,
          items: data.order_items || [],
        };

        return { data: transformedData };
      } catch (error) {
        console.error('Error fetching order:', error);
        throw error;
      }
    },
    update: async (_resource: string, params: UpdateParams) => {
      try {
        // camelCase → snake_case 변환
        const updateData: Record<string, any> = {};

        // 필드 매핑 정의
        const fieldMapping: Record<string, string> = {
          status: 'status',
          trackingNumber: 'tracking_number',
          shippingCompany: 'shipping_company',
          adminMemo: 'admin_memo',
          shippingStatus: 'shipping_status',
          shippingMemo: 'shipping_memo',
        };

        // 업데이트 가능한 필드만 변환
        Object.keys(fieldMapping).forEach((camelKey) => {
          if (params.data[camelKey] !== undefined) {
            const snakeKey = fieldMapping[camelKey];
            updateData[snakeKey] = params.data[camelKey];
          }
        });

        // updated_at 추가
        updateData.updated_at = new Date().toISOString();

        console.log(`[DataProvider] Updating order ${params.id}:`, updateData);

        const { data, error } = await supabase
          .from('orders')
          .update(updateData)
          .eq('id', params.id)
          .select('*')
          .single();

        if (error) {
          console.error(`[DataProvider] Order update error:`, error);
          throw error;
        }

        console.log(`[DataProvider] Order update successful:`, data);

        // snake_case → camelCase 변환하여 반환
        const transformedData = {
          id: data.id,
          orderNumber: data.order_number,
          userId: data.user_id,
          userName: data.orderer_name,
          userEmail: data.orderer_email,
          userPhone: data.orderer_phone,
          ordererName: data.orderer_name,
          ordererPhone: data.orderer_phone,
          ordererEmail: data.orderer_email,
          recipientName: data.recipient_name,
          recipientPhone: data.recipient_phone,
          postcode: data.postcode,
          address: data.address,
          addressDetail: data.address_detail,
          status: data.status,
          paymentStatus: data.payment_status,
          paymentMethod: data.payment_method,
          paymentKey: data.payment_key,
          isPaid: data.payment_status === 'PAID',
          totalAmount: data.total_amount,
          subtotal: data.product_amount,
          productAmount: data.product_amount,
          shippingFee: data.shipping_fee,
          discountAmount: data.discount_amount,
          couponDiscount: data.coupon_discount,
          pointsUsed: data.points_used,
          pointUsed: data.points_used,
          finalAmount: data.final_amount,
          shippingStatus: data.shipping_status,
          shippingMemo: data.shipping_memo,
          deliveryRequest: data.shipping_memo,
          trackingNumber: data.tracking_number,
          shippingCompany: data.shipping_company,
          adminMemo: data.admin_memo,
          shippedAt: data.shipped_at,
          deliveredAt: data.delivered_at,
          paidAt: data.paid_at,
          cancelledAt: data.cancelled_at,
          refundedAt: data.refunded_at,
          created_at: data.created_at,
          updated_at: data.updated_at,
        };

        return { data: transformedData };
      } catch (error) {
        console.error(`Error updating order ${params.id}:`, error);
        throw error;
      }
    },
  },
  // 캣츄딜 관리 - API를 통해 관리
  'deal-featured': {
    getList: async (_resource: string, params: GetListParams) => {
      const { page = 1, perPage = 10 } = params.pagination || {};
      const { field = 'display_order', order = 'ASC' } = params.sort || {};
      const { filter = {} } = params;

      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const searchParams = new URLSearchParams({
          _page: page.toString(),
          _limit: perPage.toString(),
          _sort: field,
          _order: order,
        });

        Object.entries(filter).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
          }
        });

        const endpoint = `${baseUrl}/api/v1/admin/deal-featured?${searchParams.toString()}`;
        const response = await fetch(endpoint);

        if (!response.ok) {
          throw new Error(`Failed to fetch deal-featured: ${response.statusText}`);
        }

        const data = await response.json();
        const totalCount = response.headers.get('X-Total-Count');

        return {
          data: data || [],
          total: totalCount ? parseInt(totalCount) : data.length,
        };
      } catch (error) {
        console.error('Error fetching deal-featured list:', error);
        throw error;
      }
    },
    getOne: async (_resource: string, params: GetOneParams) => {
      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${baseUrl}/api/v1/admin/deal-featured/${params.id}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch deal-featured: ${response.statusText}`);
        }

        const data = await response.json();
        return { data };
      } catch (error) {
        console.error(`Error fetching deal-featured ${params.id}:`, error);
        throw error;
      }
    },
    update: async (_resource: string, params: UpdateParams) => {
      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const allowedFields = ['product_id', 'display_order', 'tag', 'deal_end_time', 'is_active'];
        const updateData: any = {};
        
        allowedFields.forEach(field => {
          if (params.data[field] !== undefined) {
            updateData[field] = params.data[field];
          }
        });

        console.log(`[DataProvider] Updating deal-featured via API:`, updateData);

        const response = await fetch(`${baseUrl}/api/v1/admin/deal-featured/${params.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[DataProvider] API error:`, errorText);
          throw new Error(`Update failed: ${response.statusText}`);
        }

        const data = await response.json();
        console.log(`[DataProvider] Update successful:`, data);
        
        return { data };
      } catch (error) {
        console.error(`Error updating deal-featured ${params.id}:`, error);
        throw error;
      }
    },
    create: async (_resource: string, params: CreateParams) => {
      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${baseUrl}/api/v1/admin/deal-featured`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params.data),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[DataProvider] API error:`, errorText);
          throw new Error(`Create failed: ${response.statusText}`);
        }

        const data = await response.json();
        return { data };
      } catch (error) {
        console.error(`Error creating deal-featured:`, error);
        throw error;
      }
    },
    delete: async (_resource: string, params: DeleteParams) => {
      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${baseUrl}/api/v1/admin/deal-featured/${params.id}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[DataProvider] API error:`, errorText);
          throw new Error(`Delete failed: ${response.statusText}`);
        }

        return { data: params.previousData as any };
      } catch (error) {
        console.error(`Error deleting deal-featured ${params.id}:`, error);
        throw error;
      }
    },
    deleteMany: async (_resource: string, params: DeleteManyParams) => {
      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';

        // 각 항목을 개별 삭제
        await Promise.all(
          params.ids.map(async (id) => {
            const response = await fetch(`${baseUrl}/api/v1/admin/deal-featured/${id}`, {
              method: 'DELETE',
            });

            if (!response.ok) {
              const errorText = await response.text();
              console.error(`[DataProvider] API error:`, errorText);
              throw new Error(`Delete failed: ${response.statusText}`);
            }
          })
        );

        return { data: params.ids };
      } catch (error) {
        console.error(`Error deleting deal-featured items:`, error);
        throw error;
      }
    },
  },
  // 회원등급 설정 - API를 통해 관리
  'membership-configs': {
    getList: async (_resource: string, params: GetListParams) => {
      const { page = 1, perPage = 10 } = params.pagination || {};
      const { field = 'displayOrder', order = 'ASC' } = params.sort || {};

      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const searchParams = new URLSearchParams({
          _page: page.toString(),
          _limit: perPage.toString(),
          _sort: field,
          _order: order,
        });

        const endpoint = `${baseUrl}/api/v1/admin/membership-configs?${searchParams.toString()}`;
        const response = await fetch(endpoint);

        if (!response.ok) {
          throw new Error(`Failed to fetch membership-configs: ${response.statusText}`);
        }

        const data = await response.json();
        const totalCount = response.headers.get('X-Total-Count');

        return {
          data: data || [],
          total: totalCount ? parseInt(totalCount) : data.length,
        };
      } catch (error) {
        console.error('Error fetching membership-configs list:', error);
        throw error;
      }
    },
    getOne: async (_resource: string, params: GetOneParams) => {
      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${baseUrl}/api/v1/admin/membership-configs/${params.id}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch membership-config: ${response.statusText}`);
        }

        const data = await response.json();
        return { data };
      } catch (error) {
        console.error(`Error fetching membership-config ${params.id}:`, error);
        throw error;
      }
    },
    update: async (_resource: string, params: UpdateParams) => {
      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${baseUrl}/api/v1/admin/membership-configs/${params.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params.data),
        });

        if (!response.ok) {
          throw new Error(`Update failed: ${response.statusText}`);
        }

        const data = await response.json();
        return { data };
      } catch (error) {
        console.error(`Error updating membership-config ${params.id}:`, error);
        throw error;
      }
    },
  },
  // 카테고리 정렬 커스텀 처리
  'categories': {
    getList: async (_resource: string, params: GetListParams) => {
      const { page = 1, perPage = 10 } = params.pagination || {};
      const { field = 'code', order = 'ASC' } = params.sort || {};
      const { filter = {} } = params;

      try {
        const baseUrl = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
        const searchParams = new URLSearchParams({
          _page: page.toString(),
          _limit: perPage.toString(),
          _sort: field,
          _order: order,
        });

        Object.entries(filter).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
          }
        });

        const endpoint =
          baseUrl.length > 0
            ? `${baseUrl}/api/v1/categories?${searchParams.toString()}`
            : `/api/v1/categories?${searchParams.toString()}`;

        const response = await fetch(endpoint);

        if (!response.ok) {
          throw new Error(`Failed to fetch categories: ${response.statusText}`);
        }

        const result = await response.json();

        return {
          data: result.data || [],
          total: result.total || result.data?.length || 0,
        };
      } catch (error) {
        console.error('Error fetching categories list:', error);
        throw error;
      }
    },
  },
};

// 커스텀 핸들러가 있으면 사용, 없으면 기본 dataProvider 사용
export const enhancedDataProvider: DataProvider = new Proxy(dataProvider, {
  get(target, prop) {
    return function (resource: string, params: any) {
      const customHandler = customHandlers[resource];
      const propKey = prop as keyof DataProvider;
      if (customHandler && customHandler[propKey]) {
        return customHandler[propKey]!(resource, params);
      }
      return target[propKey](resource, params);
    };
  },
});

export default enhancedDataProvider;
